import React, { useRef, useState } from 'react';
import { LoadingButton } from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import DynamicTable from '@atlaskit/dynamic-table';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { useJNAAppStatus } from '../../../../../services/store/jna-app-status/index.tsx';
import { useTransitioningStatus } from '../../../../../services/store/transitioning-status/index.tsx';
import { useSyncDevices } from '../../../../../services/use-sync-devices/index.tsx';
import messages from './messages.tsx';

const ROWS_PER_PAGE = 5;

export const MobileDeviceList = () => {
	const { formatMessage } = useIntl();
	const [{ values }] = useJNAAppStatus();
	const [{ areOgRulesDuplicated }] = useTransitioningStatus();
	const { sync, loading: syncLoading, syncStarted } = useSyncDevices();

	if (!values) {
		throw new Error('MobileDeviceList - data should be loaded when you render this component');
	}

	const [ids, setIds] = useState(new Set<string>());
	const lastCheckbox = useRef<HTMLInputElement>();

	const handleCopy = () => {
		const items = [...ids.values()];
		const isValid = items.length > 0;

		if (!isValid) {
			lastCheckbox.current?.setCustomValidity(formatMessage(messages.noSelectionNonFinal));
			lastCheckbox.current?.reportValidity();
			return;
		}

		sync(items);
	};

	return (
		<>
			<Box
				xcss={[
					fullWidthWrapperStyles,
					...(values.length > ROWS_PER_PAGE ? [] : [fixBottomMarginStyles]),
				]}
			>
				<DynamicTable
					head={{
						cells: [
							// Without a space in content, the styles break visually
							...(!areOgRulesDuplicated ? [{ content: ' ' }] : []),
							{
								content: formatMessage(messages.mobileDeviceNonFinal),
								isSortable: true,
								key: 'string',
							},
							{ content: formatMessage(messages.appVersionNonFinal) },
							...(areOgRulesDuplicated
								? [{ content: formatMessage(messages.statusNonFinal) }]
								: []),
						],
					}}
					rows={values
						.filter((device) => !areOgRulesDuplicated || device.synced)
						.map((value) => ({
							key: value.name + value.id,
							cells: [
								...(!areOgRulesDuplicated
									? [
											{
												content: !areOgRulesDuplicated && (
													<Checkbox
														onChange={(e) => {
															if (e.target.checked) {
																e.target?.setCustomValidity('');
																setIds((prev) => new Set(prev).add(value.id));
															} else {
																setIds((prev) => {
																	const newSet = new Set(prev);
																	newSet.delete(value.id);
																	return newSet;
																});
															}
														}}
														ref={(r) => {
															if (r && !r.checked) lastCheckbox.current = r;
														}}
														value={value.id}
														isDisabled={syncStarted || syncLoading}
														isChecked={ids.has(value.id)}
													/>
												),
											},
										]
									: []),
								{
									content: value.name,
								},
								{
									content: value.appVersion,
								},
								...(areOgRulesDuplicated
									? [
											{
												content: (
													<Lozenge appearance={value.synced ? 'success' : 'removed'}>
														{formatMessage(
															value.synced ? messages.statusOnNonFinal : messages.statusOffNonFinal,
														)}
													</Lozenge>
												),
											},
										]
									: []),
							],
						}))}
					rowsPerPage={ROWS_PER_PAGE}
					defaultPage={1}
					defaultSortKey="string"
					defaultSortOrder="ASC"
				/>
			</Box>
			{!areOgRulesDuplicated && (
				<LoadingButton onClick={handleCopy} isLoading={syncLoading} isDisabled={syncStarted}>
					{formatMessage(messages.duplicateButtonNonFinal)}
				</LoadingButton>
			)}
		</>
	);
};

const fullWidthWrapperStyles = xcss({
	width: '100%',
});

const fixBottomMarginStyles = xcss({
	marginBottom: 'space.negative.200',
});
